import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DataCollectorConfig } from '@core/models/data-collector-config.model';
import { ProcedureStep } from '@core/models/procedures.model';
import { FlowchartMode } from '@flowchart';
import { TranslocoService } from '@ngneat/transloco';
import { AlertSmsConfig } from '@procedure-steps/alert-sms/alert-sms-config.model';
import { arrayMinLength, conditionalValidator, filterTruthy } from '@utils';
import { BehaviorSubject } from 'rxjs';

import it from './i18n/alert-sms-edit.it.json';

@Component({
  templateUrl: './alert-sms-edit.component.html',
  styles: [
  ]
})
export class AlertSmsEditComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

  @ViewChild('phoneInput') phoneInput!: ElementRef;

  @Input() step?: ProcedureStep;
  @Input() mode: FlowchartMode = 'edit';
  @Input() editForm!: FormGroup;

  private explicitPhoneNrs$$ = new BehaviorSubject<string[]>([]);
  explicitPhoneNrs$ = this.explicitPhoneNrs$$.asObservable();

  private editForm$$ = new BehaviorSubject<FormGroup>(this.editForm);
  editForm$ = this.editForm$$.pipe(filterTruthy());

  private step$$ = new BehaviorSubject<ProcedureStep | undefined>(undefined);
  step$ = this.step$$.pipe(filterTruthy());

  private fromPrevStepsPhoneNrs$$ = new BehaviorSubject<DataCollectorConfig[]>([]);
  fromPrevStepsPhoneNrs$ = this.fromPrevStepsPhoneNrs$$.asObservable();

  constructor(
    private fb: FormBuilder,
    private translocoSvc: TranslocoService
  ) {
    this.translocoSvc.setTranslation(it, 'it');
  }

  ngOnInit() {
    const config = this.step?.config as AlertSmsConfig;

    let configGroup = (this.editForm.get('config') as FormGroup);
    if (!configGroup) {
      this.editForm.addControl('config', this.fb.group({}));
      configGroup = (this.editForm.get('config') as FormGroup);
    }

    configGroup.addControl('message', this.fb.control(config?.message, [Validators.required]));

    const predicate = () => {
      const explicit = configGroup.get('phoneNrs.explicit')?.value;
      const fromPrevSteps = configGroup.get('phoneNrs.fromPrevSteps')?.value;
      return explicit && explicit.length <= 0 && fromPrevSteps && fromPrevSteps.length <= 0;
    };
    configGroup.addControl('phoneNrs', this.fb.group({
      explicit: this.fb.control(config?.phoneNrs?.explicit, [conditionalValidator(predicate, arrayMinLength(1))]),
      fromPrevSteps: this.fb.control(config?.phoneNrs?.fromPrevSteps, [conditionalValidator(predicate, arrayMinLength(1))]),
    }));

    this.explicitPhoneNrs$$.next(config?.phoneNrs?.explicit || []);
    this.fromPrevStepsPhoneNrs$$.next(config?.phoneNrs?.fromPrevSteps || []);

    if (this.mode === 'view') {
      (this.editForm.get('config') as FormGroup).disable();
    }
  }

  ngAfterViewInit() {
    if (this.mode === 'edit' && this.phoneInput) {
      this.phoneInput.nativeElement.value = '';
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.editForm) {
      this.editForm$$.next(changes.editForm.currentValue);
    }
    if (changes.step) {
      this.step$$.next(changes.step.currentValue);
    }
  }
  ngOnDestroy() {
    const configGroup = (this.editForm.get('config') as FormGroup);
    if (configGroup) {
      configGroup.removeControl('phoneNrs');
      configGroup.removeControl('message');

      if (Object.keys(configGroup.controls).length <= 0) {
        this.editForm.removeControl('config');
      }
    }
  }

  addExplicitPhoneNr(value?: string) {
    if (value) {
      const phoneNrs: string[] = [...new Set([...this.explicitPhoneNrs$$.getValue(), value])];
      this.explicitPhoneNrs$$.next(phoneNrs);
      const ctl = (this.editForm.get('config.phoneNrs.explicit') as FormControl);
      ctl.setValue(phoneNrs);
      ctl.markAsTouched();
      this.phoneInput.nativeElement.value = '';
    }
  }

  removeExplicitPhoneNr(value: string) {
    const phoneNrs: string[] = this.explicitPhoneNrs$$.getValue().filter(a => a !== value);
    this.explicitPhoneNrs$$.next(phoneNrs);
    const ctl = (this.editForm.get('config.phoneNrs.explicit') as FormControl);
    ctl.setValue(phoneNrs);
    ctl.markAsTouched();
    this.phoneInput.nativeElement.value = '';
  }

  addFromPrevStepsPhoneNr(value: DataCollectorConfig) {
    const phoneNrs: DataCollectorConfig[] = [...this.fromPrevStepsPhoneNrs$$.getValue(), value];
    this.fromPrevStepsPhoneNrs$$.next(phoneNrs);
    const ctl = (this.editForm.get('config.phoneNrs.fromPrevSteps') as FormControl);
    ctl.setValue(phoneNrs);
    ctl.markAsTouched();
  }

  removeFromPrevStepsPhoneNr(index: number) {
    const phoneNrs: DataCollectorConfig[] = this.fromPrevStepsPhoneNrs$$.getValue().filter((addr, i) => i !== index);
    this.fromPrevStepsPhoneNrs$$.next(phoneNrs);
    const ctl = (this.editForm.get('config.phoneNrs.fromPrevSteps') as FormControl);
    ctl.setValue(phoneNrs);
    ctl.markAsTouched();
  }
}
